import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from 'react-bootstrap';
import ClockIcon from '@/assets/icon-clock.svg?react';

interface ThinkingProps {
  thinkingText?: string;
  isLastBlockItem?: boolean;
  remarkPlugins?: any;
  rehypePlugins?: any;
  components?: any;
  showIcon?: boolean;
  startedAt?: number;
  endedAt?: number;
}

const Thinking: React.FC<ThinkingProps> = ({
  thinkingText = '',
  isLastBlockItem = false,
  remarkPlugins = [],
  rehypePlugins = [],
  components = {},
  startedAt,
  endedAt,
}) => {
  const [isOpen, setIsOpen] = useState(isLastBlockItem);
  const [now, setNow] = useState(() => performance.now());
  const isThinking = isLastBlockItem && endedAt == null;

  const sections = useMemo(() => {
    if (!thinkingText) return [];
    return thinkingText
      .split('===SECTION_BREAK===')
      .filter(Boolean)
      .map((sectionText) => {
        const lines = sectionText.split('\n');
        const blocks: string[] = [];
        let buffer: string[] = [];
        let insideBlock = false;
        for (const line of lines) {
          if (line.includes('block-start')) {
            insideBlock = true;
            buffer = [];
            continue;
          }
          if (line.includes('block-end')) {
            insideBlock = false;
            if (buffer.length) blocks.push(buffer.join('\n').trim());
            buffer = [];
            continue;
          }
          if (insideBlock) buffer.push(line);
        }
        if (insideBlock && buffer.length) blocks.push(buffer.join('\n').trim());
        return blocks.filter(Boolean);
      });
  }, [thinkingText]);

  const allBlocks = useMemo(() => sections.flat(), [sections]);

  useEffect(() => {
    setIsOpen(isLastBlockItem);
  }, [isLastBlockItem]);

  useEffect(() => {
    if (!isThinking || startedAt == null) {
      return;
    }

    const interval = window.setInterval(() => {
      setNow(performance.now());
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
  }, [isThinking, startedAt]);

  if (allBlocks.length === 0) return null;

  // Keep updating the elapsed time while thinking.

  const durationMs = startedAt != null ? Math.max(0, (endedAt ?? now) - startedAt) : null;

  const formatDuration = (ms: number) => {
    const totalSeconds = ms / 1000;

    if (totalSeconds < 60) {
      return `${totalSeconds.toFixed(1)}s`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  };

  const label =
    durationMs != null
      ? isThinking
        ? `Thinking for ${formatDuration(durationMs)}`
        : `Thought for ${formatDuration(durationMs)}`
      : isThinking
        ? 'Thinking'
        : 'Thought process';

  return (
    <div className="mb-3">
      <Card.Title>
        <div className="exclude-copy-response thinking-card">
          <button
            type="button"
            className="thinking-header-btn d-flex w-100 align-items-center justify-content-between px-3"
            onClick={() => setIsOpen((open) => !open)}
          >
            <span className="d-inline-flex align-items-center gap-2">
              {isThinking ? (
                <span
                  className="spinner-border spinner-border-sm thinking-header-spinner"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <ClockIcon className="thinking-header-icon" />
              )}
              <span>{label}</span>
            </span>
            <FontAwesomeIcon
              icon={isOpen ? faAngleUp : faAngleDown}
              className="thinking-header-chevron"
            />
          </button>

          {isOpen && (
            <div className="thinking-timeline">
              {allBlocks.map((block, index) => {
                const isLastBlock = index === allBlocks.length - 1;
                const isThinkingActive = isThinking && isLastBlock; // glowing dot only
                return (
                  <div className="thinking-item" key={index}>
                    <div
                      className={`thinking-dot ${isThinkingActive ? 'thinking-dot--active' : ''}`}
                    />
                    {!isLastBlock && <span className="thinking-line" />}
                    <div className="thinking-content">
                      <ReactMarkdown
                        remarkPlugins={remarkPlugins}
                        rehypePlugins={rehypePlugins}
                        components={components}
                      >
                        {block}
                      </ReactMarkdown>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card.Title>
    </div>
  );
};

export default Thinking;
